import type { ReactElement } from 'react';
import React, { useEffect, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  FormField,
  FormRow,
  FormSection,
  ModalFooter,
  Select,
  View,
  Text,
  FormTitle,
} from '../ui';
import { Controller, useForm } from 'react-hook-form';
import * as zod from 'zod';
import LocationSelect from '../LocationSelect';
import { OffHireType } from '../../graphql';
import { offHireTypes } from '../../constants/offHireTypes';
import capitalize from 'lodash/capitalize';
import startCase from 'lodash/startCase';
import moment from 'moment';
import { CreateOffHire } from '../core/Core';
import type { ScheduleDataContext } from '../core/ScheduleDataContext';
import type { OffHireEntry } from '@seaber/common/core/entries';
import { dateStringToNum } from '../core/util';
import { generateClientId } from '../core/ClientId';
import { useCurrentUser } from '../../state/zustand';
import LoadingSpinner from '../LoadingSpinner';

const locationSchema = zod.object({
  value: zod.number(),
  label: zod.string(),
});

const _offHireSchema = zod
  .object({
    start: zod.string(),
    end: zod.string(),
    locationA: locationSchema,
    locationB: locationSchema,
    type: zod.nativeEnum(OffHireType),
  })
  .refine((values) => values.end > values.start, {
    message: 'End time must be after start',
    path: ['end'],
  });

type OffHireFormFields = zod.infer<typeof _offHireSchema>;

export interface OffHireFormProps {
  isLoading?: boolean;
  vesselId: number;
  initialStartTime?: number;
  scheduleContext: ScheduleDataContext;
  onSave: (data: OffHireEntry) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  offHire: Partial<OffHireEntry> | null;
}

const OffHireForm = ({
  offHire,
  onSave,
  onCancel,
  onDelete,
  vesselId,
  isLoading,
  initialStartTime,
  scheduleContext,
}: OffHireFormProps): ReactElement => {
  const currentUser = useCurrentUser();

  const [isEdit] = useState(!!offHire?.locationAId);

  const locationA =
    offHire?.locationAId !== undefined
      ? scheduleContext.locations.get(offHire.locationAId)
      : null;
  const locationB =
    offHire?.locationBId !== undefined
      ? scheduleContext.locations.get(offHire.locationBId)
      : null;

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,

    formState: { errors },
  } = useForm<OffHireFormFields>({
    defaultValues: {
      start: moment(offHire?.start ?? initialStartTime)
        .startOf('hour')
        .format(),
      end: (offHire?.end
        ? moment(offHire.end)
        : moment(initialStartTime).add(1, 'days').startOf('hour')
      ).format(),
      locationA: offHire
        ? {
            label: locationA?.name ?? undefined,
            value: locationA?.id,
          }
        : undefined,
      locationB: offHire
        ? {
            label: locationB?.name ?? undefined,
            value: locationB?.id,
          }
        : undefined,
      type: offHire?.type ?? OffHireType.DRY_DOCK,
    },
  });

  const [watchedStart, watchedEnd] = watch(['start', 'end']);
  const minEndDate = moment(watchedStart).toDate();

  useEffect(() => {
    if (moment(watchedEnd).isBefore(minEndDate)) {
      setValue('end', minEndDate.toISOString());
    }
  }, [watchedStart, watchedEnd, minEndDate, setValue]);

  const errorKeys = Object.keys(errors);

  const onSubmit = (data: OffHireFormFields) => {
    if (
      currentUser === undefined ||
      currentUser?.id === undefined ||
      currentUser?.organizationId === undefined
    ) {
      throw new Error(`Cannot save offhire without current user in state`);
    }

    onSave(
      CreateOffHire({
        // From form
        start: dateStringToNum(data.start),
        end: dateStringToNum(data.end),
        locationAId: data.locationA.value,
        locationBId: data.locationB.value,
        type: data.type,
        // From existing or defaults
        dbId: offHire?.dbId ?? null,
        clientId: offHire?.clientId ?? generateClientId(),
        vesselId: offHire?.vesselId ?? vesselId,
        locked: offHire?.locked ?? false,
        shared: offHire?.shared ?? false,
        planningOrganisationId:
          offHire?.planningOrganisationId ?? currentUser.organizationId,
        userId: offHire?.userId ?? currentUser.id,
      }),
    );
  };

  const typeOptions = offHireTypes.map((t) => ({
    id: t.id,
    label: capitalize(t.name),
  }));

  return (
    <View height="100%">
      <Form
        style={{ overflowY: 'visible', borderBottom: 'none' }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormSection
          borderBottomStyle="none"
          marginTop={2}
          marginLeft={-2}
          marginBottom={-3}
        >
          <FormTitle>
            <Text fontSize="1.4em" fontWeight="bold">
              {isEdit ? 'Edit off-hire period' : 'New off-hire period'}
            </Text>
          </FormTitle>
        </FormSection>
        <FormSection zIndex={4} borderBottomStyle="none">
          <FormRow>
            <FormField
              label="Type"
              width={250}
              required
              hasError={!!errors.type}
            >
              <Select
                data-testid="type-dropdown"
                {...register('type')}
                options={typeOptions}
                placeholder="Select type"
                required
              />
            </FormField>
          </FormRow>

          <FormRow>
            <FormField
              data-testid="start port"
              label="Start port"
              width={250}
              required
              hasError={!!errors.locationA}
            >
              <Controller
                defaultValue={undefined}
                control={control}
                name="locationA"
                render={({ field }) => <LocationSelect {...field} />}
                rules={{ required: true }}
              />
            </FormField>
            <FormField
              data-testid="end port"
              label="End port"
              width={250}
              required
              hasError={!!errors.locationB}
            >
              <Controller
                defaultValue={undefined}
                control={control}
                name="locationB"
                render={({ field }) => <LocationSelect {...field} />}
                rules={{ required: true }}
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField
              label="Start time"
              data-testid="start-time-input"
              width={250}
              hasError={!!errors.start}
            >
              <Controller
                defaultValue={undefined}
                control={control}
                name="start"
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    onChange={onChange}
                    selected={value ? moment(value).toDate() : null}
                    showTime
                    showTimeInput
                    timeZone={locationA?.timezone ?? 'UTC'}
                  />
                )}
              />
            </FormField>

            <FormField
              label="End time"
              data-testid="end-time-input"
              width={250}
              hasError={!!errors.end}
            >
              <Controller
                defaultValue={undefined}
                control={control}
                name="end"
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    onChange={onChange}
                    selected={value ? moment(value).toDate() : null}
                    minDate={minEndDate}
                    showTime
                    showTimeInput
                    timeZone={
                      locationB?.timezone ?? locationA?.timezone ?? 'UTC'
                    }
                  />
                )}
              />
            </FormField>
          </FormRow>
        </FormSection>

        {errorKeys.length > 0 && (
          <FormSection>
            <Text color="#9d0300">
              {errorKeys
                .map((key) => `${startCase(key)} is not valid.`)
                .join(' ')}
            </Text>
          </FormSection>
        )}
      </Form>

      <div style={{ flexGrow: 1 }} />

      <ModalFooter>
        {onCancel && (
          <Button
            variant="negative"
            disabled={isLoading}
            onPress={onCancel}
            marginRight="3"
          >
            Discard changes & Close
          </Button>
        )}
        {isEdit && onDelete && (
          <Button
            variant="negative"
            disabled={isLoading}
            onPress={onDelete}
            marginRight="3"
          >
            Delete
          </Button>
        )}
        <div style={{ flexGrow: 1 }} />
        {isLoading && (
          <View flexDirection="row" mr={3}>
            <Text color="white" mr={3}>
              Saving changes
            </Text>
            <LoadingSpinner variant="light" />
          </View>
        )}
        <Button disabled={isLoading} onPress={handleSubmit(onSubmit)}>
          {'Save & Close'}
        </Button>
      </ModalFooter>
    </View>
  );
};

export default OffHireForm;
